import React, { FC } from "react";
import { useGetClientOrdersHistoryQuery } from "store/api";
import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { CustomStepIconProps } from "pages/OrderModal/components/CreatedOrder/types";
import { IClientOrdersHistoryProps } from "./types";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import FiberManualRecordOutlinedIcon from "@mui/icons-material/FiberManualRecordOutlined";
import {
  findOrderStatus,
  makeAddressSteps,
} from "pages/OrderModal/components/CreatedOrder/constants";
import { formatDateTime } from "utils/formatDateTime";
import { IRoute } from "store/api/types";
import { useAppDispatch } from "store/hooks/useAppDispatch";
import {
  removeSrcAddress,
  removeSrcAddressRoute,
  removeSrcBuilding,
  setSrcAddress,
  setSrcAddressRoute,
  setSrcBuilding,
} from "store/slices/srcAddress";
import { clearBoxes, setBoxes, setNoRoutes } from "store/slices/dstAddress";
import { useAppSelector } from "store/hooks/useAppSelector";
import { v4 as uuidv4 } from "uuid";
import orderStyles from "pages/OrderModal/components/CreatedOrder/CreatedOrder.styled";
import styles from "./ClientOrdersHistory.styled";

const ClientOrdersHistory: FC<IClientOrdersHistoryProps> = ({ onAction }) => {
  const { data: clientOrders, isSuccess } = useGetClientOrdersHistoryQuery({
    executionStatus: "*",
  });
  const currency = useAppSelector((state) => state.settings.currency);
  const dispatch = useAppDispatch();

  const CustomStepIcon: React.FC<CustomStepIconProps> = ({ isLast }) => {
    if (isLast) {
      return <FmdGoodOutlinedIcon color="error" />;
    }
    return <FiberManualRecordOutlinedIcon color="success" />;
  };

  const handleGoBack = (route: IRoute[]) => () => {
    dispatch(setNoRoutes(false));
    if (route.length === 1) {
      dispatch(setBoxes(route));
      dispatch(removeSrcAddress());
      dispatch(removeSrcBuilding());
      dispatch(removeSrcAddressRoute());
    }
    if (route.length > 1) {
      const reverseRoutes = [...route].reverse();
      const srcAddress = {
        name: reverseRoutes[0].name,
        ["old_name"]: "",
        houses: "",
        locale: "",
      };
      const srcBuilding = {
        house: reverseRoutes[0].number,
        lat: reverseRoutes[0].lat,
        lng: reverseRoutes[0].lng,
      };
      dispatch(setSrcAddress(srcAddress));
      dispatch(setSrcBuilding(srcBuilding));
      dispatch(setSrcAddressRoute(reverseRoutes[0]));
      const dstRoutes = reverseRoutes.slice(1);
      dispatch(setBoxes(dstRoutes));
    }
    onAction();
  };

  const handleRebook = (route: IRoute[]) => () => {
    const srcAddress = {
      name: route[0].name,
      ["old_name"]: "",
      houses: "",
      locale: "",
    };
    const srcBuilding = {
      house: route[0].number,
      lat: route[0].lat,
      lng: route[0].lng,
    };
    dispatch(setSrcAddress(srcAddress));
    dispatch(setSrcBuilding(srcBuilding));
    dispatch(setSrcAddressRoute(route[0]));
    if (route.length === 1) {
      dispatch(setNoRoutes(true));
      dispatch(clearBoxes());
    }
    if (route.length > 1) {
      const dstRoutes = route.slice(1);
      dispatch(setBoxes(dstRoutes));
    }
    onAction();
  };

  return (
    <Box sx={styles.contentWrap}>
      {isSuccess && (
        <>
          {clientOrders.map((order) => {
            const steps = makeAddressSteps(order.route);
            return (
              <Box key={uuidv4()} sx={styles.orderWrap}>
                <Box>
                  <Stepper
                    orientation="vertical"
                    sx={{ ...orderStyles.stepper, ...styles.stepper }}
                  >
                    {steps.map((step, index) => (
                      <Step key={step.label}>
                        <StepLabel
                          StepIconComponent={() => (
                            <CustomStepIcon
                              isLast={index === steps.length - 1}
                            />
                          )}
                          sx={orderStyles.stepLabel}
                        >
                          {step.label}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                  {order.required_time && (
                    <Typography sx={styles.time}>{`${formatDateTime(
                      order.required_time,
                    )}`}</Typography>
                  )}
                  <Typography
                    sx={styles.price}
                  >{`${order.order_cost}${currency}`}</Typography>
                </Box>
                <Box sx={styles.buttonsWrap}>
                  <Box sx={styles.status}>
                    {findOrderStatus(order.execution_status)?.title}
                  </Box>
                  <Button
                    type="button"
                    className="secondary"
                    sx={styles.btn}
                    onClick={handleGoBack(order.route)}
                  >
                    Їхати назад
                  </Button>
                  <Button
                    type="button"
                    className="secondary"
                    sx={styles.btn}
                    onClick={handleRebook(order.route)}
                  >
                    Повторити замовлення
                  </Button>
                </Box>
              </Box>
            );
          })}
        </>
      )}
    </Box>
  );
};

export default ClientOrdersHistory;
